import Dropdown from "components/dropdown";
import { GitMerge } from "lucide-react";
import { FaCheck, FaFolder } from "react-icons/fa";
import { MdMoreVert } from "react-icons/md";
import { formatFileSize, getFileIcon } from "utils";
import { FileSystemItem } from "types";
import { useEffect, useRef, useState } from "react";
import DocumentStatusBadge from "components/dataDisplay/DocumentStatusBadge";

export const FolderGridView = ({
  fileItems,
  handleDelete,
  onFileClick,
  selectedFiles = [],
  handleEdit,
  willUpdateUrl = true,
  setSelectedFiles,
  handleNavigate,
  isFolderView = false,
  onDeleteWorkflow,
  onBack,
  willShowCheckbox,
}: {
  fileItems: FileSystemItem[];
  handleDelete?: (item?: FileSystemItem) => void;
  onFileClick?: (item: FileSystemItem) => void;
  handleEdit?: (itemId: string, value: string) => Promise<void>;
  setSelectedFiles?: React.Dispatch<React.SetStateAction<FileSystemItem[]>>;
  selectedFiles?: FileSystemItem[];
  handleNavigate?: (item: FileSystemItem) => void;
  willUpdateUrl?: boolean;
  isFolderView?: boolean;
  onDeleteWorkflow?: (id: string) => void;
  onBack?: () => void;
  willShowCheckbox?: (item: FileSystemItem) => boolean;
}) => {
  const [editingItem, setEditingItem] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const gridRef = useRef<HTMLDivElement>(null);
  const editInputRef = useRef<HTMLInputElement>(null);

  const showCheckbox = (item: FileSystemItem) => {
    if (willShowCheckbox) return willShowCheckbox(item);
    return true;
  };

  const isFileSelected = (item: FileSystemItem) =>
    (isFolderView || item.type !== "Folder") &&
    selectedFiles.some((f) => f.id === item.id);

  const isFolderSelected = (item: FileSystemItem) =>
    !onFileClick && selectedFiles.some((f) => f.id === item.id);

  const handleCheckboxClick = (item: FileSystemItem) => {
    if (onFileClick) {
      onFileClick(item);
    }
  };

  // "Check all" logic
  const selectableItems = fileItems.filter(
    (item) => showCheckbox(item) && (isFolderView || item.type !== "Folder")
  );
  const allSelected =
    selectableItems.length > 0 &&
    selectableItems.every((item) =>
      selectedFiles.some((f) => f.id === item.id)
    );
  const someSelected =
    selectableItems.length > 0 &&
    selectableItems.some((item) => selectedFiles.some((f) => f.id === item.id));

  const handleCheckAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedFiles?.(selectableItems);
    } else {
      setSelectedFiles?.(
        selectedFiles.filter(
          (f) => !selectableItems.some((item) => item.id === f.id)
        )
      );
    }
  };

  const handleEditItem = async (item: FileSystemItem, newName: string) => {
    if (!newName.trim() || item.name === newName) return;
    await handleEdit?.(item.id, newName);
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (selectedFiles.length === 0) return;

      if (e.key === "Delete" && handleDelete) {
        e.preventDefault();
        handleDelete();
      } else if (e.key === "F2" && handleEdit) {
        e.preventDefault();
        if (selectedFiles.length === 1) {
          const item = fileItems.find(
            (item) => item.id === selectedFiles[0].id
          );
          if (item) {
            setEditingItem(item.id);
            setEditValue(item.name);
          }
        }
      } else if (e.key === "Enter" && willUpdateUrl) {
        e.preventDefault();
        if (selectedFiles.length === 1) {
          const item = fileItems.find(
            (item) => item.id === selectedFiles[0].id
          );
          if (item) {
            handleNavigate?.(item);
          }
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [
    selectedFiles,
    fileItems,
    handleDelete,
    handleEdit,
    handleNavigate,
    willUpdateUrl,
  ]);

  useEffect(() => {
    if (editingItem && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingItem]);

  return (
    <div className="relative">
      {onFileClick && (
        <div className="mt-4 flex items-center px-6">
          <label className="flex cursor-pointer items-center">
            <input
              type="checkbox"
              checked={allSelected}
              ref={(el) => {
                if (el) el.indeterminate = !allSelected && someSelected;
              }}
              onChange={handleCheckAll}
              className="peer sr-only"
            />
            <span
              className={`inline-flex h-5 w-5 items-center justify-center rounded border-2 transition-colors ${
                allSelected || someSelected
                  ? "border-blue-500 bg-blue-500 dark:border-blue-400 dark:bg-blue-500"
                  : "border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-800"
              }`}
            >
              {allSelected || someSelected ? (
                <FaCheck size={10} className="text-white dark:text-gray-900" />
              ) : null}
            </span>
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-200">
              Select All
            </span>
          </label>
        </div>
      )}

      <div
        ref={gridRef}
        className="relative grid grid-cols-2 gap-4   p-6 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6"
      >
        {onBack && (
          <div
            onClick={onBack}
            className="group relative cursor-pointer select-none transition-all"
          >
            <div className="flex aspect-square flex-col items-center justify-center rounded-lg bg-gray-50 p-4 transition-colors hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700">
              <div className="mb-2">
                <FaFolder className="h-8 w-8 text-yellow-500 dark:text-yellow-400" />
              </div>
              <span className="w-full truncate text-center text-sm font-medium text-gray-900 dark:text-gray-100">
                ...
              </span>
            </div>
          </div>
        )}
        {fileItems.map((item) => {
          const isSelected = isFileSelected(item) || isFolderSelected(item);
          const isEditing = editingItem === item.id;

          return (
            <div
              data-item-id={item.id}
              key={item.id}
              className={`group relative select-none transition-all ${
                showCheckbox(item) ? "cursor-pointer" : "cursor-not-allowed"
              }`}
            >
              <div
                onClick={() => {
                  if (willUpdateUrl) {
                    handleNavigate?.(item);
                  } else {
                    onFileClick?.(item);
                  }
                }}
                className={`flex aspect-square flex-col items-center justify-center rounded-lg p-4 transition-colors ${
                  isSelected
                    ? "bg-blue-100 dark:bg-blue-900"
                    : showCheckbox(item)
                    ? "bg-gray-50 hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700"
                    : "bg-gray-100 opacity-60 dark:bg-gray-700"
                } ${!showCheckbox(item) ? "cursor-not-allowed" : ""}`}
              >
                {showCheckbox(item) && (isFolderView || item.type !== "Folder") && (
                  <div className="absolute left-2 top-2">
                    <label
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      className="flex cursor-pointer items-center"
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleCheckboxClick(item);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="sr-only"
                      />
                      <span
                        className={`inline-flex h-5 w-5 items-center justify-center rounded border-2 transition-colors ${
                          isSelected
                            ? "border-blue-500 bg-blue-500 dark:border-blue-400 dark:bg-blue-500"
                            : "border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-800"
                        }`}
                      >
                        {isSelected ? (
                          <FaCheck
                            size={10}
                            className="text-white dark:text-gray-900"
                          />
                        ) : null}
                      </span>
                    </label>
                  </div>
                )}
                <div className="mb-2">
                  {item.type === "Folder" ? (
                    <FaFolder className="h-8 w-8 text-yellow-500 dark:text-yellow-400" />
                  ) : item.type === "Workflow" ? (
                    <GitMerge className="h-8 w-8 text-blue-500 dark:text-blue-400" />
                  ) : (
                    <div className="h-8 w-8">{getFileIcon(item.name)}</div>
                  )}
                </div>
                <span
                  className={`w-full truncate text-center text-sm font-medium ${
                    isSelected
                      ? "text-blue-700 dark:text-blue-200"
                      : "text-gray-900 dark:text-gray-100"
                  }`}
                >
                  {isEditing ? (
                    <input
                      ref={editInputRef}
                      type="text"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onBlur={() => {
                        setEditingItem(null);
                        setEditValue("");
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleEditItem(item, editValue);
                          setEditingItem(null);
                          setEditValue("");
                        } else if (e.key === "Escape") {
                          setEditingItem(null);
                          setEditValue("");
                        }
                      }}
                      className="w-full rounded border px-2 py-1 text-center text-sm font-medium focus:border-blue-500 focus:outline-none dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:focus:border-blue-400"
                    />
                  ) : (
                    <span
                      className={`w-full truncate text-center text-sm font-medium ${
                        isSelected
                          ? "text-blue-700 dark:text-blue-200"
                          : "text-gray-900 dark:text-gray-100"
                      }`}
                    >
                      {item.name}
                    </span>
                  )}
                </span>

                {item.type !== "Folder" && (
                  <span
                    className={`w-full truncate text-center text-sm font-medium ${
                      isSelected
                        ? "text-blue-700 dark:text-blue-200"
                        : "text-gray-900 dark:text-gray-100"
                    }`}
                  >
                    {formatFileSize(item.document_metadata?.file_size ?? 0)}
                  </span>
                )}
                
                {item.type === "Document" && item.status && (
                  <div className="mt-1 flex justify-center">
                    <DocumentStatusBadge status={item.status} />
                  </div>
                )}
              </div>

              {handleDelete && (
                <EditItemDropdown
                  handleDelete={handleDelete}
                  item={item}
                  setEditValue={setEditValue}
                  setEditingItem={setEditingItem}
                  onDeleteWorkflow={onDeleteWorkflow}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const EditItemDropdown = ({
  setEditingItem,
  setEditValue,
  item,
  handleDelete,
  onDeleteWorkflow,
}: {
  setEditingItem: (s: string) => void;
  setEditValue: (s: string) => void;
  item: FileSystemItem;
  handleDelete: (item?: FileSystemItem) => void;
  onDeleteWorkflow?: (id: string) => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <Dropdown
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      button={
        <MdMoreVert className="h-5 w-5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" />
      }
      children={
        <div className="flex w-28 flex-col items-start justify-start rounded-xl bg-white bg-cover bg-no-repeat shadow-xl shadow-shadow-500 dark:!bg-navy-700 dark:text-white dark:shadow-none">
          <button
            onClick={() => {
              setEditingItem(item.id);
              setEditValue(item.name);
            }}
            className="w-full rounded-t-xl px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-600"
          >
            Rename
          </button>
          <button
            onClick={() => {
              if (item.type === "Workflow") {
                if (onDeleteWorkflow) onDeleteWorkflow(item.id);
              } else {
                handleDelete(item);
              }
            }}
            className="w-full rounded-b-xl px-3 py-2 text-left text-sm text-red-500 hover:bg-gray-100 dark:hover:bg-gray-600"
          >
            Delete
          </button>
        </div>
      }
      classNames={"py-2 top-4 -left-[100px] w-max"}
    />
  );
};
